import { useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, Download, Loader2, ShieldAlert, CheckCircle2,
  AlertTriangle, ArrowLeft,
} from "lucide-react";

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const STATUS_META = {
  valid: { label: "VALID", cls: "bg-emerald-100 text-emerald-700" },
  new: { label: "NEW", cls: "bg-emerald-100 text-emerald-700" },
  update: { label: "UPDATE", cls: "bg-sky-100 text-sky-700" },
  duplicate: { label: "DUPLICATE", cls: "bg-amber-100 text-amber-800" },
  error: { label: "ERROR", cls: "bg-rose-100 text-rose-700" },
};

export function ImportDialog({ open, onOpenChange, config, onDone }) {
  const inputRef = useRef(null);
  const [step, setStep] = useState("upload"); // upload | preview | done
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep("upload"); setFile(null); setBusy(false);
    setSummary(null); setRows([]); setConfirmOpen(false); setResult(null);
  };

  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(config.templateEndpoint, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not download template")); }
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const contentBase64 = await toBase64(file);
      const res = await api.post(config.previewEndpoint, { filename: file.name, contentBase64 });
      const data = res.data || {};
      const list = data[config.rowsKey] || data.rows || [];
      setSummary(data.summary || null);
      setRows(list.map((r) => ({ ...r, _decision: r.status === "duplicate" ? "existing" : undefined })));
      setStep("preview");
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setBusy(false); }
  };

  const setDecision = (idx, d) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, _decision: d } : r)));

  const commit = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const body = config.buildCommitBody(rows);
      const res = await api.post(config.commitEndpoint, body);
      setResult(res.data || {});
      setStep("done");
      toast.success(config.successText);
      onDone && onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
    finally { setBusy(false); }
  };

  const errorCount = rows.filter((r) => r.status === "error").length;
  const commitCount = rows.filter((r) => r.status !== "error").length;

  return (
    <>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-3xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-amber-700" /> {config.title}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Ledger-backed import. Rows are validated on Preview — nothing is saved until you confirm.
            </DialogDescription>
          </DialogHeader>

          {step === "upload" && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600">
                <span className="font-medium text-stone-700">Step 1 —</span> download the template, fill it in Excel, then upload below.
                <Button data-testid="import-download-template" size="sm" variant="outline" className="ml-auto h-8 gap-1.5" onClick={downloadTemplate}>
                  <Download className="h-3.5 w-3.5" /> Download Template
                </Button>
              </div>
              {config.templateNote && (
                <p className="text-[11px] leading-relaxed text-muted-foreground">{config.templateNote}</p>
              )}
              <div data-testid="import-dropzone" onClick={() => inputRef.current?.click()}
                className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-stone-300 bg-stone-50/50 py-10 text-center hover:border-amber-400 hover:bg-amber-50/40">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700"><UploadCloud className="h-6 w-6" /></div>
                {file ? <div className="text-sm font-medium text-stone-800">{file.name}</div>
                  : <><div className="font-display text-base font-semibold text-stone-800">Select the filled template</div><div className="mt-1 text-xs text-muted-foreground">Excel .xlsx</div></>}
                <input ref={inputRef} data-testid="import-file-input" type="file" accept={config.accept || ".xlsx"} className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
                <Button data-testid="import-preview-btn" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={!file || busy} onClick={runPreview}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Preview
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === "preview" && (
            <div className="space-y-4">
              {summary && (
                <div className="flex flex-wrap gap-2" data-testid="import-summary">
                  {Object.entries(summary).map(([k, v]) => (
                    <span key={k} className="rounded-md border border-stone-200 bg-stone-50 px-2.5 py-1 text-xs font-medium text-stone-700">
                      {k}: <strong>{v}</strong>
                    </span>
                  ))}
                </div>
              )}
              <div className="overflow-x-auto rounded-lg border border-stone-200">
                <table className="w-full min-w-[700px] text-sm">
                  <thead className="border-b border-stone-200 bg-stone-50 text-left text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    <tr>
                      {config.previewColumns.map((c) => (
                        <th key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
                      ))}
                      <th className="px-3 py-2">Status</th>
                      {config.supportsDuplicateResolution && <th className="px-3 py-2">Resolve</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {rows.map((r, i) => {
                      const meta = STATUS_META[r.status] || { label: String(r.status || "").toUpperCase(), cls: "bg-stone-100 text-stone-600" };
                      return (
                        <tr key={i} data-testid={`import-row-${i}`} className={r.status === "error" ? "bg-rose-50/40" : ""}>
                          {config.previewColumns.map((c) => (
                            <td key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right font-mono text-xs" : "text-stone-800"}`}>
                              {c.fmt === "currency" ? (r[c.key] != null ? fmtCurrency(r[c.key]) : "—") : (r[c.key] ?? "—")}
                            </td>
                          ))}
                          <td className="px-3 py-2">
                            <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${meta.cls}`}>{meta.label}</span>
                            {(r.message || r.issue) && <div className={`mt-0.5 text-[10px] ${r.status === "error" ? "font-medium text-rose-600" : "text-stone-400"}`}>{r.message || r.issue}</div>}
                          </td>
                          {config.supportsDuplicateResolution && (
                            <td className="px-3 py-2">
                              {r.status === "duplicate" ? (
                                <div className="flex gap-1 text-[11px]">
                                  <button data-testid={`import-dup-existing-${i}`} onClick={() => setDecision(i, "existing")}
                                    className={`rounded px-1.5 py-0.5 ${r._decision !== "new" ? "bg-sky-600 text-white" : "bg-stone-100 text-stone-600"}`}>Use existing</button>
                                  <button data-testid={`import-dup-new-${i}`} onClick={() => setDecision(i, "new")}
                                    className={`rounded px-1.5 py-0.5 ${r._decision === "new" ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-600"}`}>Create new</button>
                                </div>
                              ) : <span className="text-[11px] text-stone-300">—</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {errorCount > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  <AlertTriangle className="h-4 w-4" /> {errorCount} row(s) have errors and will be skipped.
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" className="gap-1.5" onClick={() => setStep("upload")}><ArrowLeft className="h-4 w-4" /> Back</Button>
                <Button data-testid="import-commit-btn" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy || commitCount === 0} onClick={() => setConfirmOpen(true)}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm import ({commitCount})
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === "done" && (
            <div className="space-y-4 py-4 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
              <div className="font-display text-lg font-semibold text-stone-900">Import complete</div>
              {result && (
                <div className="flex flex-wrap justify-center gap-2" data-testid="import-result">
                  {Object.entries(result).filter(([, v]) => typeof v === "number" || typeof v === "string").map(([k, v]) => (
                    <span key={k} className="rounded-md border border-stone-200 bg-stone-50 px-2.5 py-1 text-xs text-stone-700">{k}: <strong>{String(v)}</strong></span>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">{config.successText}</p>
              <DialogFooter>
                <Button data-testid="import-done-close" className="mx-auto bg-amber-700 hover:bg-amber-800" onClick={() => close(false)}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" /> Confirm import to live ERP
            </AlertDialogTitle>
            <AlertDialogDescription>{config.confirmText}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="import-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="import-confirm-commit" className="bg-emerald-600 hover:bg-emerald-700" onClick={commit}>
              Yes, import {commitCount}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
